import React, { Fragment, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux';

import { useParams, useNavigate } from 'react-router-dom';

import { closeModal, openModal } from '../../store/slices/modal';

import { useAssignTitleToExpertConversation, useCreateExpertChat, useGenerateExpertChatAiResponse, useGetExpertConversations, useRegenerateExpertChatAiResponseById } from '../../hooks/users';
import { useSocket } from '../../providers/SocketProvider';

import MainLayout from '../layouts/Main'
import InboxListItem from '../sub_components/immigration_help/InboxListItem';
import Chat from '../sub_components/immigration_help/Chat';
import Suggestion from '../sub_components/immigration_help/Suggestion';
import SourceItem from '../sub_components/immigration_help/SourceItem';
import { SVGIcon } from '../wrappers/SVGIcon';

import { EAssetMetaSourceType, EExpertChatSource, IChatAiResponse, IExpertChat, IExpertConversationWithChatsForUser, IResponseSource } from '../../interfaces/expert_chats';

import { Icons } from '../../data/Assets';

const ImmigrationHelp = () => {
    const dispatch = useDispatch();

    const navigate = useNavigate();

    const { id } = useParams();

    const [message, setMessage] = useState('');
    const [expertConversations, setExpertConversations] = useState<IExpertConversationWithChatsForUser[]>([]);
    const [selectedExpertConversation, setSelectedExpertConversation] = useState<IExpertConversationWithChatsForUser | null>(null);
    const [isUserTyping, setIsUserTyping] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isGeneratingAiResponse, setIsGeneratingAiResponse] = useState(false);
    const [isRegeneratingAiResponse, setIsRegeneratingAiResponse] = useState<number | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [aiResponses, setAiResponses] = useState<IChatAiResponse[]>([]);
    const [linkSources, setLinkSources] = useState<IResponseSource[]>([]);
    const [documentSources, setDocumentSources] = useState<IResponseSource[]>([]);
    const [showSources, setShowSources] = useState(false);

    const textAreaRef = useRef<HTMLTextAreaElement>(null);
    const generateResponseTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const { data: expertConversationsData } = useGetExpertConversations();

    const { mutate: createExpertChat } = useCreateExpertChat();
    const { mutate: generateExpertChatAiResponse } = useGenerateExpertChatAiResponse();
    const { mutate: regenerateExpertChatAiResponse } = useRegenerateExpertChatAiResponseById();
    const { mutate: assignTitleToExpertConversation } = useAssignTitleToExpertConversation();

    const { socket } = useSocket();

    useEffect(() => {
        const pageViews = document.querySelector('.immersion__main_layout__page_views') as HTMLElement;
        if (pageViews) {
            pageViews.style.padding = '0px';
        }

        const footerTextarea = document.querySelector('.immersion__immigration_help__content__footer__textarea') as HTMLTextAreaElement;
        if (footerTextarea) {
            footerTextarea.addEventListener('input', () => {
                footerTextarea.style.height = 'auto';
                footerTextarea.style.height = footerTextarea.scrollHeight + 'px';
            });
        }
    }, []);

    useEffect(() => {
        if (!id || !expertConversationsData) return;

        setExpertConversations(expertConversationsData);

        const expertConversation = expertConversationsData.find((expertConversation) => expertConversation.id === parseInt(id));
        if (expertConversation) {
            setSelectedExpertConversation(expertConversation);

            if (socket) {
                socket.emit('joinConversation', {
                    room_id: expertConversation.id,
                    instance_id: expertConversation.instance_id,
                });
            };
        };
    }, [id, expertConversationsData]); // eslint-disable-line

    const onEditMessage = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        if (socket && selectedExpertConversation) {
            socket.emit('typing', {
                room_id: selectedExpertConversation?.id,
                instance_id: selectedExpertConversation?.instance_id,
                source: EExpertChatSource.EXPERT,
                is_typing: true,
            });
        }
        setMessage(e.target.value);
    };

    const onStopTyping = () => {
        if (socket && selectedExpertConversation) {
            socket.emit('typing', {
                room_id: selectedExpertConversation?.id,
                instance_id: selectedExpertConversation?.instance_id,
                source: EExpertChatSource.EXPERT,
                is_typing: false,
            });
        }
    };

    const onEditSuggestion = (response: string) => {
        setMessage(response);
    };

    const onSendSuggestion = (response: string) => {
        if (!selectedExpertConversation) {
            return;
        };

        dispatch(openModal({
            name: 'MESSAGE_MODAL',
            data: {
                view: 'CONFIRMATION',
                closeBtn: false,
                title: "Confirm send AI response?",
                content: 'Check your message before sending it to the student.',
                actionTxt: "Send",
                cancelTxt: "Edit",
                action: () => handleSendMessage(response),
            }
        }));
    };

    const onRegenerateSuggestion = (aiResponseId: number) => {
        setShowSources(false);
        setIsRegeneratingAiResponse(aiResponseId);

        regenerateExpertChatAiResponse({
            payload: {
                chat_ai_response_id: aiResponseId,
            },
            setErrorMessage: setErrorMessage,
            successCallback: (data) => {
                const oldAiResponses = aiResponses.filter((aiResponse) => aiResponse.id !== aiResponseId);
                setAiResponses([...oldAiResponses, data]);
                setIsRegeneratingAiResponse(null);
            }
        });
    };

    const handleSendMessage = (message: string) => {
        if (!selectedExpertConversation) {
            return;
        };

        createExpertChat({
            payload: {
                create_expert_chat: {
                    expert_conversation_id: selectedExpertConversation.id,
                    source: EExpertChatSource.EXPERT,
                    message: message,
                }
            },
            setIsProcessing: setIsProcessing,
            setErrorMessage: setErrorMessage,
            successCallback: (data) => {
                setSelectedExpertConversation({
                    ...selectedExpertConversation,
                    expert_chats: [...selectedExpertConversation.expert_chats, data],
                });

                setExpertConversations(prev => prev.map(expertConversation =>
                    expertConversation.id === selectedExpertConversation.id ? { ...expertConversation, expert_chats: [...expertConversation.expert_chats, data] } : expertConversation
                ));

                setMessage('');

                setAiResponses([]);

                if (socket) {
                    socket.emit('sendMessage', {
                        room_id: selectedExpertConversation.id,
                        instance_id: selectedExpertConversation.instance_id,
                        expert_chat: data,
                    });
                }

                dispatch(closeModal('MESSAGE_MODAL'));
            }
        });
    }

    const onSendMessage = () => {
        if (isProcessing) {
            return;
        };

        if (!message) {
            return;
        };

        dispatch(openModal({
            name: 'MESSAGE_MODAL',
            data: {
                view: 'CONFIRMATION',
                closeBtn: false,
                title: "Confirm send message?",
                content: 'Check your message before sending it to the student.',
                actionTxt: "Send",
                cancelTxt: "Edit",
                action: () => handleSendMessage(message),
            }
        }));
    };

    const onViewStudentInformation = () => {
        if (!selectedExpertConversation) {
            return;
        };

        navigate(`/students/${selectedExpertConversation.student_id}/personal`);
    };

    const onShowSources = () => {
        if (!showSources)
            setShowSources(true);
    };

    const onNewChat = () => {
        dispatch(openModal({
            name: 'NEW_CHAT_MODAL',
            data: {
                view: 'NEW_CHAT',
            }
        }));
    };

    const onGenerateAiResponse = () => {
        if (!selectedExpertConversation) {
            return;
        };

        setShowSources(false);
        generateExpertChatAiResponse({
            payload: {
                expert_conversation_id: selectedExpertConversation.id,
            },
            setIsProcessing: setIsGeneratingAiResponse,
            setErrorMessage: setErrorMessage,
            successCallback: (data) => {
                setAiResponses([...data]);
            }
        });
    };

    const isGenerateAiResponseDisabled = () => {
        const lastMessage = selectedExpertConversation?.expert_chats[selectedExpertConversation.expert_chats.length - 1];
        if (!lastMessage) return true;
        if (isGeneratingAiResponse || lastMessage?.source === EExpertChatSource.EXPERT) {
            return true;
        }

        return false;
    };

    const onAssignTitleToExpertConversation = () => {
        if (!selectedExpertConversation) {
            return;
        };

        if (selectedExpertConversation.title !== 'New Conversation') {
            return;
        };

        assignTitleToExpertConversation({
            payload: {
                expert_conversation_id: selectedExpertConversation.id,
            },
            setErrorMessage: setErrorMessage,
            successCallback: (data) => {
                setSelectedExpertConversation(prev => prev ? {
                    ...prev,
                    title: data,
                } : null);

                setExpertConversations(prev => prev.map(expertConversation =>
                    expertConversation.id === selectedExpertConversation.id ? { ...expertConversation, title: data, expert_chats: selectedExpertConversation.expert_chats } : expertConversation
                ));
            }
        });
    };

    useEffect(() => {
        if (!socket || !selectedExpertConversation) {
            return;
        };

        socket.on('receiveMessage', (data: IExpertChat) => {
            if (data.source === EExpertChatSource.EXPERT) return;

            const oldExpertChats = selectedExpertConversation.expert_chats;

            setSelectedExpertConversation({
                ...selectedExpertConversation,
                expert_chats: [...oldExpertChats, data],
            });

            setExpertConversations(prev => prev.map(expertConversation =>
                expertConversation.id === selectedExpertConversation.id ? { ...expertConversation, expert_chats: [...oldExpertChats, data] } : expertConversation
            ));
        });

        socket.on('typing', (data) => {
            if (data.source === EExpertChatSource.EXPERT) return;

            setIsUserTyping(data.is_typing);
        });
    }, [socket, selectedExpertConversation]);

    useEffect(() => {
        if (!selectedExpertConversation) return;

        const lastMessage = selectedExpertConversation.expert_chats[selectedExpertConversation.expert_chats.length - 1];
        if (!lastMessage || lastMessage?.source === EExpertChatSource.EXPERT) return;

        if (generateResponseTimeoutRef.current) {
            clearTimeout(generateResponseTimeoutRef.current);
        }

        onAssignTitleToExpertConversation();

        generateResponseTimeoutRef.current = setTimeout(() => {
            onGenerateAiResponse();
        }, 1000);

        return () => {
            if (generateResponseTimeoutRef.current) {
                clearTimeout(generateResponseTimeoutRef.current);
            }
        };
    }, [selectedExpertConversation]); // eslint-disable-line


    useEffect(() => {
        if (aiResponses.length > 0) {
            setLinkSources(aiResponses[0].response_sources.filter((source) => source.asset_meta?.source_type === EAssetMetaSourceType.LINK));
            setDocumentSources(aiResponses[0].response_sources.filter((source) => source.asset_meta?.source_type === EAssetMetaSourceType.DOCUMENT));
        } else {
            setLinkSources([]);
            setDocumentSources([]);
        }
    }, [aiResponses]);

    return (
        <MainLayout>
            <div className='immersion__immigration_help'>
                <div className='immersion__immigration_help__sidebar'>
                    <div className='immersion__immigration_help__inbox'>
                        <div className='immersion__immigration_help__inbox__header'>
                            <h2 className='immersion__immigration_help__inbox__header__title'>
                                Chats
                            </h2>
                            <div className='immersion__immigration_help__inbox__header__actions'>
                                <button className='immersion__btn immersion__btn__primary immersion__immigration_help__inbox__header__actions__button mice' onClick={onNewChat}>
                                    New
                                </button>
                            </div>
                        </div>
                        <div className='immersion__immigration_help__inbox__list'>
                            {expertConversations.map((expertConversation) => (
                                <InboxListItem
                                    key={expertConversation.id}
                                    expertConversation={expertConversation}
                                    selected={selectedExpertConversation?.id === expertConversation.id}
                                />
                            ))}
                        </div>
                    </div>
                </div>
                <div className={`immersion__immigration_help__content ${!selectedExpertConversation ? 'immersion__immigration_help__content__empty' : ''}`}>
                    {selectedExpertConversation ? (
                        <Fragment>
                            <div className='immersion__immigration_help__content__header'>
                                <div className='immersion__immigration_help__content__header__metadata'>
                                    <div className='immersion__immigration_help__content__header__metadata__avatar'>
                                        <SVGIcon src={Icons.UserCircleFill} />
                                    </div>
                                    <div className='immersion__immigration_help__content__header__metadata__content'>
                                        <h4 className='immersion__immigration_help__content__header__metadata__content__name'>
                                            {selectedExpertConversation.student_name}
                                        </h4>
                                        <p className='immersion__immigration_help__content__header__metadata__content__role mice'>
                                            Student Details to be decided
                                        </p>
                                    </div>
                                </div>
                                <div className='immersion__immigration_help__content__header__actions'>
                                    <button className='immersion__btn immersion__immigration_help__content__header__actions__button mice' onClick={onViewStudentInformation}>
                                        View Student Information
                                    </button>
                                </div>
                            </div>
                            <div className='immersion__immigration_help__content__body'>
                                {!selectedExpertConversation.expert_chats.length && (
                                    <div className='immersion__immigration_help__content__body__empty'>
                                        <h4 className='immersion__immigration_help__content__body__empty__title'>No messages yet</h4>
                                        <p className='immersion__immigration_help__content__body__empty__description'>Send a message to {selectedExpertConversation.student_name}</p>
                                    </div>
                                )}
                                <div className='immersion__immigration_help__content__body__chats'>
                                    {selectedExpertConversation.expert_chats.map(expertChat => (
                                        <Chat
                                            key={expertChat.id}
                                            expert_chat={expertChat}
                                        />
                                    ))}
                                    {isUserTyping && (
                                        <Chat
                                            expert_chat={{
                                                id: 0,
                                                source: EExpertChatSource.USER,
                                                expert_conversation_id: selectedExpertConversation.id,
                                                message: 'Typing...',
                                                created_at: new Date(),
                                            }}
                                        />
                                    )}
                                    {!isGeneratingAiResponse && aiResponses.length > 0 &&
                                        <div className='immersion__immigration_help__suggestions'>
                                            <div className='immersion__immigration_help__suggestions__header'>
                                                <h5>Which response do you prefer?</h5>
                                            </div>
                                            <div className='immersion__immigration_help__suggestions__list'>
                                                {aiResponses.map((aiResponse, index) => (
                                                    <Suggestion
                                                        key={aiResponse.id}
                                                        title={`Response ${index + 1}`}
                                                        description={aiResponse.response}
                                                        disclaimer={'This is not legal advice. If you want to be certain about your situation, we recommend connecting with a licensed immigration lawyer or consultant.'}
                                                        hasSource={aiResponse.response_sources.length > 0}
                                                        isLoading={isRegeneratingAiResponse === aiResponse.id}
                                                        onEdit={onEditSuggestion}
                                                        onSend={onSendSuggestion}
                                                        onRegenerate={() => onRegenerateSuggestion(aiResponse.id)}
                                                        onShowSources={onShowSources}
                                                    />
                                                ))}
                                            </div>
                                        </div>
                                    }
                                    {isGeneratingAiResponse &&
                                        <div className='immersion__immigration_help__content__body__generating_ai_response'>
                                            <br />
                                            <h4>Generating AI response...</h4>
                                            <i className='immersion__btn_loader' />
                                        </div>
                                    }
                                </div>
                            </div>
                        </Fragment>)
                        : (
                            <Fragment>
                                <h4 className='immersion__immigration_help__content__empty__title'>No chats yet</h4>
                                <p className='immersion__immigration_help__content__empty__description'>Start a new chat with a student</p>
                            </Fragment>
                        )
                    }
                    <div className='immersion__immigration_help__content__footer'>
                        <textarea
                            ref={textAreaRef}
                            className='immersion__immigration_help__content__footer__textarea'
                            placeholder='Type your message here...'
                            autoFocus={true}
                            value={message}
                            onChange={onEditMessage}
                            onBlur={onStopTyping}
                        />
                        <div className='immersion__immigration_help__content__footer__enhancements'>
                            <button
                                className='immersion__btn immersion__immigration_help__content__footer__enhancements__button immersion__immigration_help__content__footer__enhancements__button__disabled'
                                disabled={true}
                            >
                                Fix spelling/grammar
                            </button>
                            <button
                                className='immersion__btn immersion__immigration_help__content__footer__enhancements__button immersion__immigration_help__content__footer__enhancements__button__disabled'
                                disabled={true}
                            >
                                Rephrase text
                            </button>
                            <button
                                className={`immersion__btn immersion__immigration_help__content__footer__enhancements__button ${isGenerateAiResponseDisabled() ? 'immersion__immigration_help__content__footer__enhancements__button__disabled' : ''}`}
                                disabled={isGenerateAiResponseDisabled()}
                                onClick={onGenerateAiResponse}
                            >
                                AI response
                            </button>
                        </div>
                        <div className='immersion__immigration_help__content__footer__actions'>
                            <button disabled={true} className='immersion__btn immersion__immigration_help__content__footer__actions__button immersion__immigration_help__content__footer__actions__button__primary'>
                                Ask co-pilot
                            </button>
                            <button disabled={true} className='immersion__btn immersion__immigration_help__content__footer__actions__button'>
                                Ask a colleague
                            </button>
                            <button disabled={true} className='immersion__btn immersion__immigration_help__content__footer__actions__button__icon'>
                                <SVGIcon src={Icons.Mic} hoverColor='dark_grey' />
                            </button>
                            <button className='immersion__btn immersion__immigration_help__content__footer__actions__button__icon'>
                                <SVGIcon src={Icons.Attach} hoverColor='dark_grey' />
                            </button>
                            <button className='immersion__btn immersion__immigration_help__content__footer__actions__button__icon' onClick={onSendMessage}>
                                <SVGIcon src={Icons.SendFill} hoverColor='dark_grey' />
                            </button>
                        </div>
                    </div>
                </div>

                {showSources && (documentSources.length > 0 || linkSources.length > 0) &&
                    <div className='immersion__immigration_help__sources'>
                        <div className='immersion__immigration_help__sources__header'>
                            <h4 className='immersion__immigration_help__sources__header__title'>
                                Sources
                            </h4>
                        </div>
                        <div className='immersion__immigration_help__sources__list'>
                            {linkSources.length > 0 && <h4 className='immersion__immigration_help__sources__list__title'>Links</h4>}
                            {linkSources.map((source) => (
                                <SourceItem
                                    key={source.id}
                                    source={source}
                                />
                            ))}
                            {documentSources.length > 0 && <h4 className='immersion__immigration_help__sources__list__title'>Documents</h4>}
                            {documentSources.map((source) => (
                                <SourceItem
                                    key={source.id}
                                    source={source}
                                />
                            ))}
                        </div>
                    </div>
                    //     : <div className='immersion__immigration_help__sources immersion__immigration_help__sources__empty'>
                    //     <h4 className='immersion__immigration_help__sources__empty__title'>No sources yet</h4>
                    //     <p className='immersion__immigration_help__sources__empty__description mice'>Generate an AI response to the student's message</p>
                    // </div>
                }
            </div>
        </MainLayout>
    )
}

export default ImmigrationHelp;